import React, { useState, useEffect } from 'react';
import { MessageCircle, Zap, Server, Key, RefreshCw, Search, AlertCircle, CheckCircle, Copy, ExternalLink, Globe, Plus } from 'lucide-react';
import { useAuth } from '../lib/AuthContext';
import { supabase } from '../lib/supabase';
import { ExternalAgentRegistration } from './ExternalAgentRegistration';

interface A2AAgent {
  agent_id: string;
  name: string;
  description: string;
  category: string;
  capabilities: string[];
  endpoint: string;
  a2a_key?: string;
  is_owned?: boolean;
}

export function A2AProtocolDashboard() {
  const [a2aAgents, setA2AAgents] = useState<A2AAgent[]>([]);
  const [ownedAgents, setOwnedAgents] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCapabilities, setSelectedCapabilities] = useState<string[]>([]);
  const [copiedKey, setCopiedKey] = useState<string | null>(null);
  const [registrationStatus, setRegistrationStatus] = useState<{agentId: string, status: 'idle' | 'loading' | 'success' | 'error', message?: string}[]>([]);
  const [showExternalAgents, setShowExternalAgents] = useState(false);
  const { user } = useAuth();

  const allCapabilities = [
    'text_generation',
    'data_analysis',
    'question_answering',
    'data_visualization',
    'code_generation',
    'translation',
    'summarization',
    'image_generation',
    'sentiment_analysis',
    'classification'
  ];

  useEffect(() => {
    if (user) {
      fetchOwnedAgents();
      discoverA2AAgents();
    }
  }, [user]);

  const fetchOwnedAgents = async () => {
    try {
      const { data, error } = await supabase
        .from('user_agents')
        .select(`
          id,
          agent_id,
          agents (
            id,
            name,
            description,
            category,
            metadata
          ),
          configuration
        `)
        .eq('user_id', user?.id);
      
      if (error) throw error;
      
      setOwnedAgents(data || []);
      
      // Update registration status
      setRegistrationStatus(data?.map((agent: any) => ({
        agentId: agent.agent_id,
        status: agent.configuration?.a2a_key ? 'success' : 'idle'
      })) || []);
    } catch (err: any) {
      console.error('Error fetching owned agents:', err);
    }
  };

  const discoverA2AAgents = async () => {
    setLoading(true);
    setError(null);
    
    try {
      const response = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/a2a-protocol-handler/discover`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${import.meta.env.VITE_SUPABASE_ANON_KEY}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            capabilities: selectedCapabilities.length > 0 ? selectedCapabilities : undefined
          })
        }
      );

      if (!response.ok) {
        throw new Error('Failed to discover A2A agents');
      }

      const data = await response.json();
      
      if (data.success) {
        // Mark owned agents
        const agents = data.agents.map((agent: A2AAgent) => ({
          ...agent,
          is_owned: ownedAgents.some((ownedAgent: any) => ownedAgent.agent_id === agent.agent_id)
        }));
        
        setA2AAgents(agents);
      } else {
        throw new Error(data.error || 'Failed to discover A2A agents');
      }
    } catch (err: any) {
      console.error('Error discovering A2A agents:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const registerAgentWithA2A = async (agentId: string, endpoint: string = 'https://example.com/a2a') => {
    // Update status to loading
    setRegistrationStatus(prev => 
      prev.map(status => 
        status.agentId === agentId 
          ? { ...status, status: 'loading' } 
          : status
      )
    );
    
    try {
      const response = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/a2a-protocol-handler/register`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${import.meta.env.VITE_SUPABASE_ANON_KEY}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            agent_id: agentId,
            endpoint,
            capabilities: ['text_generation', 'question_answering'] // Default capabilities
          })
        }
      );

      if (!response.ok) {
        throw new Error('Failed to register agent with A2A');
      }

      const data = await response.json();
      
      if (data.success) {
        // Update registration status
        setRegistrationStatus(prev => 
          prev.map(status => 
            status.agentId === agentId 
              ? { ...status, status: 'success', message: 'Registered successfully' } 
              : status
          )
        );
        
        // Refresh owned agents to get updated configuration
        fetchOwnedAgents();
        
        // Refresh A2A agents list
        discoverA2AAgents();
      } else {
        throw new Error(data.error || 'Failed to register agent with A2A');
      }
    } catch (err: any) {
      console.error('Error registering agent with A2A:', err);
      
      // Update status to error
      setRegistrationStatus(prev => 
        prev.map(status => 
          status.agentId === agentId 
            ? { ...status, status: 'error', message: err.message } 
            : status
        )
      );
    }
  };

  const copyA2AKey = (agentId: string, key: string) => {
    navigator.clipboard.writeText(key);
    setCopiedKey(agentId);
    setTimeout(() => setCopiedKey(null), 2000);
  };

  const filteredAgents = a2aAgents.filter(agent => {
    // Apply search filter
    if (searchQuery && !agent.name.toLowerCase().includes(searchQuery.toLowerCase()) && 
        !agent.description.toLowerCase().includes(searchQuery.toLowerCase())) {
      return false;
    }
    
    // Apply capabilities filter
    if (selectedCapabilities.length > 0) {
      return selectedCapabilities.every(cap => agent.capabilities.includes(cap));
    }
    
    return true;
  });

  const getAgentRegistrationStatus = (agentId: string) => {
    return registrationStatus.find(status => status.agentId === agentId) || { agentId, status: 'idle' };
  };

  const getAgentA2AKey = (agentId: string) => {
    const agent = ownedAgents.find((a: any) => a.agent_id === agentId);
    return agent?.configuration?.a2a_key;
  };

  return (
    <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
      <div className="bg-gradient-to-r from-blue-600 to-purple-600 p-6 text-white">
        <div className="flex items-center gap-3">
          <MessageCircle className="w-8 h-8" />
          <div>
            <h2 className="text-2xl font-bold">A2A Protocol Dashboard</h2>
            <p className="opacity-90">Manage your Agent-to-Agent communications</p>
          </div>
        </div>
      </div>
      
      <div className="p-6 space-y-8">
        {/* Your Agents Section */}
        <section>
          <h3 className="text-xl font-semibold text-gray-900 mb-4">Your Agents</h3>
          
          {ownedAgents.length === 0 ? (
            <div className="bg-gray-50 border border-gray-200 rounded-lg p-6 text-center">
              <Server className="w-12 h-12 text-gray-400 mx-auto mb-3" />
              <h4 className="font-medium text-gray-900 mb-2">No Agents Found</h4>
              <p className="text-gray-600 mb-4">You don't have any agents yet. Create an agent to enable A2A communication.</p>
              <button
                onClick={() => window.location.href = '/create-agent'}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
              >
                Create Agent
              </button>
            </div>
          ) : (
            <div className="space-y-4">
              {ownedAgents.map((agent: any) => {
                const status = getAgentRegistrationStatus(agent.agent_id);
                const a2aKey = getAgentA2AKey(agent.agent_id);
                const isRegistered = status.status === 'success' || !!a2aKey;
                
                return (
                  <div key={agent.agent_id} className="border border-gray-200 rounded-lg overflow-hidden">
                    <div className="flex items-center justify-between p-4 bg-gray-50">
                      <div className="flex items-center gap-3">
                        <div className="p-2 bg-blue-100 rounded-lg">
                          <Server className="w-5 h-5 text-blue-600" />
                        </div>
                        <div>
                          <h4 className="font-medium text-gray-900">{agent.agents.name}</h4>
                          <p className="text-xs text-gray-500">{agent.agents.category}</p>
                        </div>
                      </div>
                      
                      {isRegistered ? (
                        <span className="px-3 py-1 bg-green-100 text-green-700 rounded-lg text-xs font-medium flex items-center gap-1">
                          <CheckCircle className="w-3 h-3" />
                          A2A Enabled
                        </span>
                      ) : (
                        <button
                          onClick={() => registerAgentWithA2A(agent.agent_id)}
                          disabled={status.status === 'loading'}
                          className="px-3 py-1 bg-blue-600 text-white rounded-lg text-xs font-medium hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-1"
                        >
                          {status.status === 'loading' ? (
                            <>
                              <RefreshCw className="w-3 h-3 animate-spin" />
                              Registering...
                            </>
                          ) : (
                            <>
                              <Zap className="w-3 h-3" />
                              Enable A2A
                            </>
                          )}
                        </button>
                      )}
                    </div>
                    
                    {isRegistered && (
                      <div className="p-4 border-t border-gray-200">
                        <div className="mb-3">
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            A2A Key
                          </label>
                          <div className="flex">
                            <input
                              type="password"
                              value={a2aKey ? "•".repeat(20) : ""}
                              readOnly
                              className="flex-1 px-3 py-2 border border-gray-300 rounded-l-lg bg-gray-50 text-gray-500 text-sm"
                            />
                            <button
                              onClick={() => copyA2AKey(agent.agent_id, a2aKey)}
                              className="px-3 py-2 bg-blue-600 text-white rounded-r-lg hover:bg-blue-700 transition-colors"
                            >
                              {copiedKey === agent.agent_id ? (
                                <CheckCircle className="w-4 h-4" />
                              ) : (
                                <Copy className="w-4 h-4" />
                              )}
                            </button>
                          </div>
                          <p className="mt-1 text-xs text-gray-500">
                            Use this key in the X-A2A-Key header when sending messages
                          </p>
                        </div>
                        
                        <div className="flex items-center justify-between">
                          <div className="text-xs text-gray-500">
                            Registered: {new Date(agent.agents.metadata?.a2a_registered_at || Date.now()).toLocaleDateString()}
                          </div>
                          <button
                            onClick={() => window.location.href = `/agent/${agent.agent_id}/a2a-settings`}
                            className="text-xs text-blue-600 hover:text-blue-700 flex items-center gap-1"
                          >
                            <Settings className="w-3 h-3" />
                            A2A Settings
                          </button>
                        </div>
                      </div>
                    )}
                    
                    {status.status === 'error' && (
                      <div className="p-3 bg-red-50 border-t border-red-200 text-red-700 text-xs">
                        <div className="flex items-center gap-1">
                          <AlertCircle className="w-3 h-3" />
                          <span>Error: {status.message}</span>
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </section>
        
        {/* Discover A2A Agents Section */}
        <section>
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-xl font-semibold text-gray-900">Discover A2A Agents</h3>
            <button
              onClick={discoverA2AAgents}
              disabled={loading}
              className="flex items-center gap-2 px-3 py-1 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-sm disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? (
                <RefreshCw className="w-4 h-4 animate-spin" />
              ) : (
                <RefreshCw className="w-4 h-4" />
              )}
              Refresh
            </button>
          </div>
          
          {/* Search and Filter */}
          <div className="flex flex-wrap items-center gap-4 mb-4 p-4 bg-gray-50 rounded-lg">
            <div className="flex-1 min-w-64">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="Search agents..."
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Capabilities
              </label>
              <div className="flex flex-wrap gap-2">
                {allCapabilities.slice(0, 5).map((capability) => (
                  <label key={capability} className="flex items-center gap-1">
                    <input
                      type="checkbox"
                      checked={selectedCapabilities.includes(capability)}
                      onChange={(e) => {
                        if (e.target.checked) {
                          setSelectedCapabilities([...selectedCapabilities, capability]);
                        } else {
                          setSelectedCapabilities(selectedCapabilities.filter(c => c !== capability));
                        }
                      }}
                      className="rounded text-blue-600"
                    />
                    <span className="text-xs text-gray-700">{capability.replace('_', ' ')}</span>
                  </label>
                ))}
              </div>
            </div>
          </div>
          
          {error && (
            <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg text-red-700 flex items-center gap-2">
              <AlertCircle className="w-5 h-5 flex-shrink-0" />
              <span>{error}</span>
            </div>
          )}
          
          {/* Agents List */}
          {loading ? (
            <div className="text-center py-12">
              <RefreshCw className="w-12 h-12 text-blue-600 animate-spin mx-auto mb-4" />
              <p className="text-gray-600">Discovering A2A agents...</p>
            </div>
          ) : filteredAgents.length === 0 ? (
            <div className="text-center py-12 bg-gray-50 rounded-lg">
              <MessageCircle className="w-12 h-12 text-gray-400 mx-auto mb-4" />
              <h4 className="font-medium text-gray-900 mb-2">No A2A Agents Found</h4>
              <p className="text-gray-600">
                {searchQuery || selectedCapabilities.length > 0
                  ? "No agents match your search criteria. Try adjusting your filters."
                  : "There are no agents with A2A capabilities available yet."}
              </p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {filteredAgents.map((agent) => (
                <div key={agent.agent_id} className="border border-gray-200 rounded-lg overflow-hidden">
                  <div className="p-4">
                    <div className="flex items-center justify-between mb-3">
                      <h4 className="font-medium text-gray-900">{agent.name}</h4>
                      {agent.is_owned && (
                        <span className="px-2 py-1 bg-blue-100 text-blue-700 rounded text-xs">
                          Your Agent
                        </span>
                      )}
                    </div>
                    
                    <p className="text-sm text-gray-600 mb-3">{agent.description}</p>
                    
                    <div className="mb-3">
                      <div className="text-xs font-medium text-gray-700 mb-1">Capabilities:</div>
                      <div className="flex flex-wrap gap-1">
                        {agent.capabilities.map((capability, index) => (
                          <span key={index} className="px-2 py-1 bg-blue-50 text-blue-600 rounded text-xs">
                            {capability.replace('_', ' ')}
                          </span>
                        ))}
                      </div>
                    </div>
                    
                    <div className="flex items-center justify-between">
                      <span className="text-xs text-gray-500">Category: {agent.category}</span>
                      
                      <a
                        href={`/agent/${agent.agent_id}`}
                        className="flex items-center gap-1 text-xs text-blue-600 hover:text-blue-700"
                      >
                        <ExternalLink className="w-3 h-3" />
                        View Agent
                      </a>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </section>
        
        {/* Documentation Link */}
        <section>
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 flex items-start gap-3">
            <div className="p-2 bg-blue-100 rounded-lg">
              <Zap className="w-5 h-5 text-blue-600" />
            </div>
            <div>
              <h4 className="font-medium text-blue-900 mb-1">Learn More About A2A</h4>
              <p className="text-sm text-blue-700 mb-3">
                Check out our comprehensive documentation on implementing the A2A protocol in your agents.
              </p>
              <a
                href="/a2a-documentation"
                className="text-sm text-blue-600 hover:text-blue-700 font-medium flex items-center gap-1"
              >
                <ExternalLink className="w-4 h-4" />
                View A2A Documentation
              </a>
            </div>
          </div>
        </section>
        
        {/* External Agent Integration */}
        <section>
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-xl font-semibold text-gray-900">External Agent Integration</h3>
            <button
              onClick={() => setShowExternalAgents(true)}
              className="flex items-center gap-2 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors"
            >
              <Plus className="w-4 h-4" />
              Add External Agent
            </button>
          </div>
          
          <div className="bg-purple-50 border border-purple-200 rounded-lg p-4">
            <div className="flex items-center gap-2 text-purple-700 mb-2">
              <Globe className="w-5 h-5" />
              <span className="font-medium">Connect External Agents</span>
            </div>
            <p className="text-purple-600 text-sm mb-3">
              Integrate agents from other platforms (OpenAI, Anthropic, custom webhooks) into your A2A network.
            </p>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3 text-xs text-purple-700">
              <div>• OpenAI Assistants API</div>
              <div>• Anthropic Claude API</div>
              <div>• Custom Webhooks</div>
              <div>• Zapier Integration</div>
              <div>• Make.com Workflows</div>
              <div>• Any REST API</div>
            </div>
          </div>
        </section>
      </div>
      
      {/* External Agent Registration Modal */}
      <ExternalAgentRegistration
        isOpen={showExternalAgents}
        onClose={() => setShowExternalAgents(false)}
      />
    </div>
  );
}

// Small Settings icon component
function Settings(props: React.SVGProps<SVGSVGElement>) {
  return (
    <svg
      {...props}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <circle cx="12" cy="12" r="3" />
      <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1 0 2.83 2 2 0 0 1-2.83 0l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-2 2 2 2 0 0 1-2-2v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83 0 2 2 0 0 1 0-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1-2-2 2 2 0 0 1 2-2h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 0-2.83 2 2 0 0 1 2.83 0l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 2-2 2 2 0 0 1 2 2v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 0 2 2 0 0 1 0 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 2 2 2 2 0 0 1-2 2h-.09a1.65 1.65 0 0 0-1.51 1z" />
    </svg>
  );
}